import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { btn, outlineBtn, card } from "@/components/org/intake/intakeMeta";

export const ReviewStep = ({ model, onSaved }) => {
  const { API } = useAuth();
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadReadiness = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/org/intake/models/${model.model_id}/readiness`, { withCredentials: true });
      setReadiness(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, model.model_id]);

  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  const act = async (action) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/org/intake/models/${model.model_id}/${action}`, {}, { withCredentials: true });
      onSaved(data.model);
      loadReadiness();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={card} data-testid="wizard-step-review">
      <p className="text-sm font-bold text-slate-950 dark:text-white">Review &amp; Activate</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Fix anything flagged below, then activate. QR codes become available for active models.</p>

      {readiness && (
        <div className="mt-4 space-y-2" data-testid="wizard-review-readiness">
          {readiness.ready ? (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="wizard-review-ready">
              <CheckCircle2 size={16} /> This Intake Model is ready to activate.
            </div>
          ) : (
            readiness.errors.map((e, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400" data-testid={`wizard-review-error-${idx}`}>
                <AlertTriangle size={16} /> {e}
              </div>
            ))
          )}
        </div>
      )}

      {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="wizard-review-action-error">{error}</p>}

      <div className="mt-5 flex items-center gap-2">
        {model.status === "draft" && (
          <button type="button" onClick={() => act("activate")} disabled={busy || !readiness?.ready} data-testid="wizard-review-activate-btn" className={btn}>
            {busy && <Loader2 size={14} className="animate-spin" />} Activate
          </button>
        )}
        {model.status === "active" && (
          <button type="button" onClick={() => act("deactivate")} disabled={busy} data-testid="wizard-review-deactivate-btn" className={outlineBtn}>
            {busy && <Loader2 size={14} className="animate-spin" />} Deactivate
          </button>
        )}
        {model.status === "inactive" && (
          <button type="button" onClick={() => act("reactivate")} disabled={busy || !readiness?.ready} data-testid="wizard-review-reactivate-btn" className={btn}>
            {busy && <Loader2 size={14} className="animate-spin" />} Reactivate
          </button>
        )}
      </div>
    </div>
  );
};
